import { isEqual, uniqueId } from "lodash";
import React, { FC, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ExportProviderAvailabilityType, ExportType } from "@app/exports/enumerations";
import { ExportDataRouteState } from "@app/exports/exportsRouteState";
import { EntityTypeEnum, LocationConfidenceScoresLabelEnum, NetworkAssessmentAnalysisType, ProjectPerspective, ProjectionProviderType, TimeFrequency } from "@app/projects/enumerators";
import AdequacyFilters from "@app/projects/network-assessment/adequacy/adequacyFilters";
import { getPrimaryAndSecondaryRunConfigurations } from "@app/projects/network-assessment/adequacy/utility";
import { ClaimsActivityLineOfBusinessTypeEnum, ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { DeleteProjectionProvider, ProjectionPanelMode, ProjectionProviderDetails } from "@app/projects/projection-provider";
import projectionProviderDetailsReducer, {
  projectionProviderDetailsInitialState
} from "@app/projects/projection-provider/projectionProviderDetailsReducer";
import { convertServicingProvidersToAccessPoints } from "@app/projects/projection-provider/utils";
import AccessSummary from "@app/projects/results/adequacy/accessSummary";
import { NetNewProvider } from "@app/projects/results/servicing-provider/NetNewProvider";
import { toast } from "@app/projects/servicing-provider/DistanceResultToast";
import NetNewServicingProviderService from "@app/projects/servicing-provider/NetNewServicingProviderService";
import { NetNewServicingProviderTable } from "@app/projects/servicing-provider/NetNewServicingProviderTable";
import { buildCommonImpactProviderColumns } from "@app/projects/servicing-provider/ServicingProviderHelpers";
import ServicingProviderProfile from "@app/projects/servicing-provider/ServicingProviderProfile";
import { ACCESS_POINT_PROFILE_FLYOUT_TITLE } from "@app/projects/servicing-provider/servicingProviderProfileConstants";
import { SimulateProviderTargeting } from "@app/projects/servicing-provider/SimulateProviderTargeting";
import { CareJourneyScoresValueEnum } from "@app/projects/servicing-provider/provider-claims/CareJourneyScoresValueEnum";
import { EffectivenessEfficiencyValueEnum } from "@app/projects/servicing-provider/provider-claims/EffectivenessEfficiencyValueEnum";
import GainsPercentFilter from "@app/projects/servicing-provider/provider-claims/GainsPercentFilter";
import { MotiveScoreValueEnum } from "@app/projects/servicing-provider/provider-claims/MotiveScoreValueEnum";
import { NewPatientClaimsEnum } from "@app/projects/servicing-provider/provider-claims/NewPatientClaimsEnum";
import ConfidenceTierSearchFilters from "@app/projects/servicing-provider/provider-claims/ConfidenceTierSearchFilters";
import ProviderClaimsFilters from "@app/projects/servicing-provider/provider-claims/ProviderClaimsFilters";
import { SpecialtyPredictionModelEnum } from "@app/projects/servicing-provider/provider-claims/SpecialtyPredictionModelEnum";
import TopCondition from "@app/projects/servicing-provider/provider-claims/TopCondition";
import TopProcedure from "@app/projects/servicing-provider/provider-claims/TopProcedure";
import { GainsPercentEnum, NPF, NetNewServicingProviderTableData } from "@app/projects/servicing-provider/types";
import { RegionGranularity } from "@app/region-sets";
import { ReportCustomizationRouteState } from "@app/report-customization/build-reports/interfaces/reportCustomizationRouteState";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";
import { Flyout, FlyoutContext, FlyoutLink } from "@app/shared/Flyouts";
import FlyoutContextProvider from "@app/shared/Flyouts/FlyoutContext";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { QESButton } from "@app/shared/qes-standards/Button";
import { SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import GeographyService from "@app/geography/geographyService";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { RegionFilterItem } from "@app/projects/adequacySharedProjectContextProps";
import Project from "@app/projects/project";
import { ProjectRunConfigurationResult } from "@app/projects/projectRunConfigurationResult";
import ProjectionProvider from "@app/projects/results/projection-provider/projectionProvider";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { SearchBox } from "@app/shared/SearchBox";
import { Toggle } from "@app/shared/Toggle";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { capitalizeFirstLetter } from "@app/shared/utility/capitalizeFirstLetter";
import RoleRestricted from "@app/user/RoleRestricted";
import { UserContext } from "@app/user/UserContext";
import Roles from "@app/user/roles";
import { ViewableField, TableType } from "@app/viewable-fields/viewableFields";

type DisplayedProviderType = "targeted" | "nonTargeted";

interface NetNewServicingProvidersPanelProps {
  accessSummary: AccessSummary
  editRestricted: boolean;
  networkAssessmentAnalysisType: NetworkAssessmentAnalysisType;
  processProviderChanges: (doItNow: boolean) => void;
  project: Project;
  providerType: ProviderNetworkTypeEnum;
  readOnly: boolean;
  regionSetId: number;
  runConfiguration: ProjectRunConfigurationResult;
  setProjectHasUnprocessedProjectionProviders: (doesIt: boolean) => void;
  specialtyGroupCode: string;
  viewableFields: ViewableField[];
  exportEndpoint: string;
  showProviderValidations?: boolean;
  showSpecialtyPredictionModel?: boolean;
  showPgrConfidenceTier?: boolean;
}

const NetNewServicingProvidersPanel: FC<React.PropsWithChildren<NetNewServicingProvidersPanelProps>> = (props: NetNewServicingProvidersPanelProps) => {
  const [displayedProviderType, setDisplayedProviderType] = useState<DisplayedProviderType>("targeted");
  const [displayedProviderTypeChanged, setDisplayedProviderTypeChanged] = useState<boolean>(false);
  const [displayedProviderTypeDefault, setDisplayedProviderTypeDefault] = useState<boolean>(false);
  const [displayedProviderData, setDisplayedProviderData] = useState<NetNewServicingProviderTableData[]>(undefined);
  const [lastSearchValue, setLastSearchValue] = useState("");
  const [selectedColumn, setSelectedColumn] = useState<string>(undefined);
  const [sortDirection, setSortDirection] = useState<SortDirections>(undefined);
  const [selectedEffectivenessValues, setSelectedEffectivenessValues] = useState<EffectivenessEfficiencyValueEnum[]>(undefined);
  const [selectedEfficiencyValues, setSelectedEfficiencyValues] = useState<EffectivenessEfficiencyValueEnum[]>(undefined);
  const [selectedVolumeLabels, setSelectedVolumeLabels] = useState<ClaimsActivityVolumeLabelEnum[]>(undefined);
  const [selectedLineOfBusiness, setSelectedLineOfBusiness] = useState<ClaimsActivityLineOfBusinessTypeEnum>(undefined);
  const [selectedLocationConfidenceValues, setSelectedLocationConfidenceValues] = useState<LocationConfidenceScoresLabelEnum[]>(undefined);
  const [selectedMotiveAppropriatenessValues, setSelectedMotiveAppropriatenessValues] = useState<MotiveScoreValueEnum[]>(undefined);
  const [selectedNewPatientClaimsValues, setSelectedNewPatientClaimsValues] = useState<NewPatientClaimsEnum[]>(undefined);
  const [selectedProcedureValues, setSelectedProcedureValues] = useState<string[]>(undefined);
  const [selectedCareJourneyCostValues, setSelectedCareJourneyCostValues] = useState<CareJourneyScoresValueEnum[]>(undefined);
  const [selectedCareJourneyQualityValues, setSelectedCareJourneyQualityValues] = useState<CareJourneyScoresValueEnum[]>(undefined);
  const [selectedConditionValues, setSelectedConditionValues] = useState<string[]>(undefined);
  const [selectedSpecialtyPredictionModelFilter, setSelectedSpecialtyPredictionModelFilter] = useState<SpecialtyPredictionModelEnum>(undefined);
  const [selectedPgrConfidenceTiers, setSelectedPgrConfidenceTiers] = useState<number[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [skip, setSkip] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);
  const netNewServicingProviderService = useRef(new NetNewServicingProviderService()).current;
  const geoService = useRef(new GeographyService()).current;
  const userContext = useContext(UserContext);
  const flyoutContext = useContext(FlyoutContext);
  const projectContext = useContext(CountyProjectContext);
  const [servicingProviders, setServicingProviders] = useState<NetNewProvider[]>([]);
  const [gainsPercent, setGainsPercent] = useState<GainsPercentEnum>(GainsPercentEnum.All);
  const [disabledGainsFilter, setDisabledGainsFilter] = useState<boolean>(false);
  const [projectionProvidersState, projectionProvidersReducer] = useReducer(projectionProviderDetailsReducer, projectionProviderDetailsInitialState);
  const [selectedNetNewProvider, setSelectedNetNewProvider] = useState<ProjectionProvider>(undefined);
  const [selectedFlyoutButtonRef, setSelectedFlyoutButtonRef] = useState<number>(-1);
  const flyoutButtonRefs = useRef<React.RefObject<HTMLButtonElement>[]>([]);
  const take = 50;
  const toastId = uniqueId("distance-toast-");
  const [topProcedures, setTopProcedures] = useState<DropdownItem<TopProcedure>[]>([]);
  const [topConditions, setTopConditions] = useState<DropdownItem<TopCondition>[]>([]);


  const providerPlurality = useMemo(() => {
    return selectedProviders.length === 1 ? "provider" : "providers";
  }, [selectedProviders]);

  const selectedServicingProviders = useMemo(() => {
    return servicingProviders;
  }, [servicingProviders, selectedProviders]);

  useEffect(() => {
    // prevent it from calling before the accordion opens
    if (displayedProviderData) {
      fetchProviders(displayedProviderType);
    }
    setSelectedProviders([]);
    projectContext.setShouldRefreshNetworkProviders(false);
  }, [projectContext.shouldRefreshNetNewProviders, props.viewableFields]);

  useEffect(() => handleRunConfigurationChange(), [projectContext.runConfiguration.isProjected, projectContext.runConfiguration.isProjectedTermed]);

  useEffect(() => setProvidersData(servicingProviders), [servicingProviders]);

  const fetchProviders = (
    shouldGetTargeted?: string,
    newSkip?: number,
    newSelectedColumn?: keyof NetNewServicingProviderTableData,
    newSortDirection?: SortDirections,
    newSearchValue?: string,
    newGainsPercent: GainsPercentEnum = gainsPercent,
    newEffectivenessValues?: EffectivenessEfficiencyValueEnum[],
    newEfficiencyValues?: EffectivenessEfficiencyValueEnum[],
    newVolumeLabels?: ClaimsActivityVolumeLabelEnum[],
    newLineOfBusiness?: ClaimsActivityLineOfBusinessTypeEnum,
    newLocationConfidenceValues?: LocationConfidenceScoresLabelEnum[],
    newMotiveAppropriatenessValues?: MotiveScoreValueEnum[],
    newNewPatientClaimsValues?: NewPatientClaimsEnum[],
    newSelectedTopProcedures?: string[],
    newCareJourneyCostScoreValues?: CareJourneyScoresValueEnum[],
    newCareJourneyQualityScoreValues?: CareJourneyScoresValueEnum[],
    newSelectedTopConditions?: string[],
    newSpecialtyPredictionModelFilter?: SpecialtyPredictionModelEnum,
    newPgrConfidenceTiers?: number[],
  ) => {
    setLoading(true);

    if (props.project.showPciData &&
      newLineOfBusiness === undefined &&
      newVolumeLabels === undefined &&
      newEffectivenessValues === undefined &&
      newEfficiencyValues === undefined &&
      newLocationConfidenceValues === undefined &&
      newMotiveAppropriatenessValues === undefined &&
      newNewPatientClaimsValues === undefined &&
      newSelectedTopProcedures === undefined &&
      newCareJourneyCostScoreValues === undefined &&
      newCareJourneyQualityScoreValues === undefined &&
      newSelectedTopConditions === undefined
    ) {
      // pci-specific defaults for on load
      newEffectivenessValues = Object.values(EffectivenessEfficiencyValueEnum);
      newEfficiencyValues = Object.values(EffectivenessEfficiencyValueEnum);
      newLineOfBusiness = ClaimsActivityLineOfBusinessTypeEnum.Total;
      newVolumeLabels = [...Object.values(ClaimsActivityVolumeLabelEnum).filter(x => x !== ClaimsActivityVolumeLabelEnum.Total).map(x => x)];
      newLocationConfidenceValues = Object.values(LocationConfidenceScoresLabelEnum);
      newMotiveAppropriatenessValues = Object.values(MotiveScoreValueEnum);
      newNewPatientClaimsValues = Object.values(NewPatientClaimsEnum);
      newSelectedTopProcedures = [];
      newCareJourneyCostScoreValues = Object.values(CareJourneyScoresValueEnum);
      newCareJourneyQualityScoreValues = Object.values(CareJourneyScoresValueEnum);
      newSelectedTopConditions = [];
    }
    if (userContext.roles.includes(Roles.NET_NEW_PROVIDER_USER)) {
      netNewServicingProviderService.getNetNewServicingProviders(
        props.project?.projectId,
        props.runConfiguration?.projectRunConfigurationId,
        props.regionSetId,
        props.specialtyGroupCode,
        props.networkAssessmentAnalysisType,
        newGainsPercent,
        shouldGetTargeted,
        newSelectedColumn as string || selectedColumn,
        newSortDirection || sortDirection,
        newSearchValue !== undefined ? newSearchValue : lastSearchValue, // if newSearchValue is "" we want to apply that
        newEffectivenessValues !== undefined ? newEffectivenessValues : selectedEffectivenessValues,
        newEfficiencyValues !== undefined ? newEfficiencyValues : selectedEfficiencyValues,
        newVolumeLabels !== undefined ? newVolumeLabels : selectedVolumeLabels,
        newLineOfBusiness !== undefined ? newLineOfBusiness : selectedLineOfBusiness,
        newLocationConfidenceValues !== undefined ? newLocationConfidenceValues : selectedLocationConfidenceValues,
        newMotiveAppropriatenessValues !== undefined ? newMotiveAppropriatenessValues : selectedMotiveAppropriatenessValues,
        newNewPatientClaimsValues !== undefined ? newNewPatientClaimsValues : selectedNewPatientClaimsValues,
        newSelectedTopProcedures !== undefined ? newSelectedTopProcedures : selectedProcedureValues,
        newCareJourneyCostScoreValues !== undefined ? newCareJourneyCostScoreValues : selectedCareJourneyCostValues,
        newCareJourneyQualityScoreValues !== undefined ? newCareJourneyQualityScoreValues : selectedCareJourneyQualityValues,
        newSelectedTopConditions !== undefined ? newSelectedTopConditions : selectedConditionValues,
        newSpecialtyPredictionModelFilter !== undefined ? newSpecialtyPredictionModelFilter : selectedSpecialtyPredictionModelFilter,
        newPgrConfidenceTiers !== undefined ? newPgrConfidenceTiers : selectedPgrConfidenceTiers,
      ).then((newProviderData) => {
        //TODO: Once arguments are added to the API we can just pass the displayedProviderType in instead of filtering here in UI

        setServicingProviders(newProviderData.providers);

        if (!isEqual(newProviderData?.topConditions.map(x => x.code), topConditions.map(x => x.key.code))) {
          const mappedTopConditions: DropdownItem<TopCondition>[] = newProviderData?.topConditions.map(x => {
            return { key: x, name: `${x.code} - ${x.description}` }
          });
          setTopConditions(mappedTopConditions);
        }

        if (!isEqual(newProviderData?.topProcedures.map(x => x.code), topProcedures.map(x => x.key.code))) {
          const mappedTopProcedures: DropdownItem<TopProcedure>[] = newProviderData?.topProcedures.map(x => {
            return { key: x, name: `${x.code} - ${x.description}` }
          });
          setTopProcedures(mappedTopProcedures);
        }

        if (gainsPercent != newGainsPercent) {
          setProvidersData(newProviderData.providers);
        }
        else if (!displayedProviderTypeDefault){
          setProviderType(newProviderData.providers);
          setDisplayedProviderTypeDefault(true);
        }

        if (!!newGainsPercent) setGainsPercent(newGainsPercent);
        if (newSkip === 0 || !!newSkip) setSkip(newSkip);
        if (newSelectedColumn) setSelectedColumn(newSelectedColumn as string);
        if (newSortDirection) setSortDirection(newSortDirection);
        if (newSearchValue !== undefined) setLastSearchValue(newSearchValue);
        if (newEffectivenessValues !== undefined) setSelectedEffectivenessValues(newEffectivenessValues);
        if (newEfficiencyValues !== undefined) setSelectedEfficiencyValues(newEfficiencyValues);
        if (newVolumeLabels !== undefined) setSelectedVolumeLabels(newVolumeLabels);
        if (newLineOfBusiness !== undefined) setSelectedLineOfBusiness(newLineOfBusiness);
        if (newLocationConfidenceValues !== undefined) setSelectedLocationConfidenceValues(newLocationConfidenceValues);
        if (newMotiveAppropriatenessValues !== undefined) setSelectedMotiveAppropriatenessValues(newMotiveAppropriatenessValues);
        if (newNewPatientClaimsValues !== undefined) setSelectedNewPatientClaimsValues(newNewPatientClaimsValues);
        if (newSelectedTopProcedures !== undefined) setSelectedProcedureValues(newSelectedTopProcedures);
        if (newCareJourneyCostScoreValues !== undefined) setSelectedCareJourneyCostValues(newCareJourneyCostScoreValues);
        if (newCareJourneyQualityScoreValues !== undefined) setSelectedCareJourneyQualityValues(newCareJourneyQualityScoreValues);
        if (newSelectedTopConditions !== undefined) setSelectedConditionValues(newSelectedTopConditions);
        if (newSpecialtyPredictionModelFilter !== undefined) setSelectedSpecialtyPredictionModelFilter(newSpecialtyPredictionModelFilter);
        if (newPgrConfidenceTiers !== undefined) setSelectedPgrConfidenceTiers(newPgrConfidenceTiers);
        const newFlyoutRefs = [];
        for (const provider of newProviderData.providers) {
          newFlyoutRefs[provider.providerId] = React.createRef<HTMLButtonElement>();
        }
        flyoutButtonRefs.current = newFlyoutRefs;
        setLoading(false);
      });
    }
  };

  const isFieldVisible = (fieldName: NPF) => {
    return props.viewableFields?.find((vf) => vf.backingFieldName === fieldName)?.visible;
  }

  const getProviderProfile = (provider: NetNewProvider) => {
    return (
      <ServicingProviderProfile
        {...props}
        isTerminated={false}
        netNewProvider={true}
        nextClosestProvider={false}
        provider={provider}
        providerNetworkType={props.providerType}
      />
    )
  }

  const convertProviderToTableData = (providers: NetNewProvider[]) => {
    return providers.map<NetNewServicingProviderTableData>((p) => {
      const { specialty, specialtyCode, address, ...commonColumns } = buildCommonImpactProviderColumns(p, props.viewableFields, props.runConfiguration);

      /* oxlint-disable sort-keys */
      const td: NetNewServicingProviderTableData = {
        [wafflesKeyColumnName]: p.netNewProviderProjectId.toString(), //TODO: Talk to Steven to see if necessary at all
        displayName: (
          <span>
            <FlyoutLink
              classNames="p-0 qes-button-secondary"
              content={{ body: getProviderProfile(p), title: ACCESS_POINT_PROFILE_FLYOUT_TITLE }}
              testId="net-new-servicing-providers-panel__provider-profile-flyout"
            >
              {p.displayName}
            </FlyoutLink>
            <br />
            {isFieldVisible(NPF.NPI) && p.npi && <span className="text-muted ml15 d-block">NPI: {p.npi}</span>}
            {isFieldVisible(NPF.OrganizationName) && p.organizationName && p.entityType === EntityTypeEnum.Individual && <span className="text-muted ml15 d-block">{p.organizationName}</span>}
          </span>
        ),
        npi: p.npi,
        specialtyCode,
        specialty,
        address,
        ...commonColumns as NetNewServicingProviderTableData,
        projectionReason: (() => {
          const netNewProvider = p as unknown as NetNewProvider;
          return (
            <>
              {!netNewProvider.processed && <div className="demibold">UNPROCESSED</div>}
              {(
                <>
                  <RoleRestricted allowedRoles={[Roles.VOLUME_TARGET]}>
                    {(props.runConfiguration.isLatestPublished) && (
                      <TooltipWrapper text="View/Edit Targeted Provider Details">
                        <QESButton
                          className="side-panel-link fw-normal text-info p-0"
                          onClick={() => handleOpenEditProviderFlyout(netNewProvider)}
                          qesTestId={`net-new-servicing-providers-panel__target-details--edit--${netNewProvider.providerId}`}
                          qesType="none"
                          qesRef={flyoutButtonRefs.current[netNewProvider.providerId]}
                        >
                          {netNewProvider.reasonName ?
                            <span title={netNewProvider.reasonName}>{netNewProvider.reasonName}</span> : "Other"}
                        </QESButton>
                      </TooltipWrapper>
                    )}
                  </RoleRestricted>
                </>
              )}
            </>
          );
        })(),
        projectionReasonDescription: <div className="net-new-providers__projection-reason">{ellipsifyDescription((p as unknown as NetNewProvider).reasonDescription)}</div>
      };
      /* oxlint-enable sort-keys */

      return td;
    });
  }

  const ellipsifyDescription = (description: string) => {
    if (!description || description.length < 250) {
      return description;
    }
    return (
      <TooltipWrapper text={description} tooltipWidth="350px">
        {description.substring(0, 250)}...
      </TooltipWrapper>
    );
  }

  const updateChangeTracker = () => {
    if (displayedProviderTypeChanged) {
      setDisplayedProviderTypeChanged(false);
    }
  }


  const handleOpenEditProviderFlyout = (netNewProvider: NetNewProvider) => {
    netNewProvider.providerId = netNewProvider.netNewProviderProjectId;
    setSelectedFlyoutButtonRef(netNewProvider.providerId);
    setSelectedNetNewProvider(netNewProvider);
    flyoutContext.setIsFlyoutVisible(true);
  };

  const accordionTitle = useMemo(() => {
    let base = `Net New Servicing Providers`;
    if (isOpen && !!displayedProviderData) {
      base += ` (${Math.min(displayedProviderData.length, totalRecords)} of ${totalRecords}${lastSearchValue ? `, filtered to ${lastSearchValue}` : ""})`;
    }
    return base;
  }, [displayedProviderData, isOpen, totalRecords]);

  const handleNetNewProviderCreatedOrUpdated = async (runProjection: boolean, hasUnprocessedProviders = true) => {
    if (runProjection) {
      props.processProviderChanges(false);
    } else {
      props.setProjectHasUnprocessedProjectionProviders(hasUnprocessedProviders);
      setSelectedProviders([]);
      fetchProviders(displayedProviderType, 0, undefined);
    }
  }

  //TODO: Look at ImpactProvidersPanel to implement correct functions in place of the empty JSX.Elements <></>
  const getBulkActionHeader = useMemo(() => {
    const allowTargetRemove = props.runConfiguration.isLatestPublished || props.runConfiguration.isProjected || props.runConfiguration.isProjectedTermed;

    const showRemoveTargeted = displayedProviderType === 'targeted' ? allowTargetRemove : false;
    const showTarget = allowTargetRemove && !showRemoveTargeted && !props.project.isEditRestricted;

    const granularity = RegionGranularity.County;
    const networkTypes = [ProviderNetworkTypeEnum.NetNew];
    const regionSetIds = [props.regionSetId];
    const specialtyGroupCodes = [props.specialtyGroupCode];
    const providerAvailabilityType = displayedProviderType === "targeted" ? ExportProviderAvailabilityType.Targeted : ExportProviderAvailabilityType.All;

    const hasSelectedRecords = selectedProviders.length > 0;
    const tableType = props.providerType.charAt(0).toUpperCase() + props.providerType.substring(1);
    const targetDisabledToolTip = '';

    /* oxlint-disable sort-keys */
    const adequacyFilters: AdequacyFilters = {
      active: false,
      all: false,
      approved: false,
      exceptions: false,
      gaps: false,
      opportunity: false,
      pending: false,
      changedGaps: null,
      specialtyType: null,
      viewBy: ProjectPerspective.All
    };
    const providerIds: number[] = [];
    const filters: RegionFilterItem[] = [];
    const projectURL = '';

    const selectedMemberships: number[] = [projectContext.runConfiguration.memberSetId];
    if (projectContext.isDualMembershipMode) {
      selectedMemberships.push(projectContext.compareToRunConfiguration.memberSetId);
    }

    const { primaryRunConfiguration, secondaryRunConfiguration } = getPrimaryAndSecondaryRunConfigurations(projectContext.project.projectRunConfigurations, projectContext.runConfiguration.projectRunConfigurationId);

    return (
      <>
        <span className="bulk-action-group">
          <Link<Partial<ExportDataRouteState> & ReportCustomizationRouteState>
            to={{
              pathname: props.exportEndpoint,
              state: {
                activeModule: "Adequacy",
                activeNetworkType: ProviderNetworkTypeEnum.NetNew,
                adequacyFilters: adequacyFilters,
                availableExportTypes: [ExportType.ServicingProviders],
                disableRegionFilters: true,
                disableSpecialtyGroupFilters: true,
                filters: filters,
                granularity,
                initialNetworkAssessmentAnalysisType: props.networkAssessmentAnalysisType,
                networkTypes,
                projectResourceId: props.project.resource.resourceId,
                projectResourceName: props.project.resource.name,
                projectURL: projectURL,
                providerAvailabilityType,
                providerIds,
                regionSetIds,
                specialtyGroupCodes,

                // new reportcustomization
                /* oxlint-disable sort-keys */
                projectName: projectContext.project.resource.name,
                defaultReportType: ReportType.AdequacyServicingProvider,
                showClosestProviderToMemberReport: projectContext.project.showClosestProviderReport,
                defaultProviderSource: ProviderNetworkTypeEnum.NetNew,
                defaultProviderFilter: displayedProviderType === "targeted" ? ExportProviderAvailabilityType.Targeted : ExportProviderAvailabilityType.All,
                networkAssessmentAnalysisType: projectContext.networkAssessmentAnalysisType,
                primaryMembership: { alias: primaryRunConfiguration.memberSetAlias || primaryRunConfiguration.memberSetName, id: primaryRunConfiguration.memberSetId },
                secondaryMembership: { alias: secondaryRunConfiguration?.memberSetAlias || secondaryRunConfiguration?.memberSetName, id: secondaryRunConfiguration?.memberSetId },
                selectedMemberships,
                selectedTimeslice: projectContext.runConfiguration.projectRunConfigurationId, // runConfigurationId
                runFrequency: projectContext.runFrequency || TimeFrequency.All,
                isCompareMode: false,
                showPatternOfCare: projectContext.project.showPatternOfCareProviders,
                hasProspectProviders: !!projectContext.runConfiguration.prospectProviderSetId,
                hasMarketProviders: !!projectContext.runConfiguration.marketProviderSetId,
                showAdvancedComparison: projectContext.project.showAdvancedComparison,
                showExpandableSpecialties: !!projectContext.adequacyResult?.details?.some((d) => d.canExpandSpecialties),
                showRegionSummaryReport: projectContext.project.hasRegions,
                /* oxlint-enable */
              },
            }}
            data-testid={`${tableType}__create-report-button`}
          >
            <QesIcon iconName={QesIcons.DownloadCloudV1} description="Download" className="align-text-top me-1" /> Create Report
          </Link>
        </span>

        <span className={`bulk-action-group ms-3`}>
          <TooltipWrapper text="Measure distance between two selected providers">
            <QESButton qesType="secondary" className="px-0" disabled={selectedProviders.length !== 2} onClick={handleDistanceClick} qesTestId={`net-new-servicing-providers-panel__distance--${ProviderNetworkTypeEnum.NetNew}`}>
              <QesIcon iconName={QesIcons.Route} description="Distance" className="align-text-top me-1" /> Distance
            </QESButton>
          </TooltipWrapper>
        </span>

        {
          (showTarget || null) && (
            <RoleRestricted allowedRoles={[Roles.VOLUME_TARGET]}>
              <span className={`bulk-action-group ms-3${(hasSelectedRecords && targetDisabledToolTip === "") ? "" : " disabled"}`}>
                <FlyoutLink
                  classNames="p-0"
                  content={{
                    body: getPreviewTargetingContent(),
                    title: `Target ${capitalizeFirstLetter(providerPlurality)}`
                  }}
                  disabled={!hasSelectedRecords || targetDisabledToolTip !== ""}
                  testId="net-new-servicing-providers-panel__target-flyout--with-preview"
                >
                  <TooltipWrapper text={targetDisabledToolTip === '' ? `Preview and target selected ${providerPlurality}` : targetDisabledToolTip}>
                    <QesIcon iconName={QesIcons.TargetV4} description="Target" className="align-text-top me-1" /> Target
                  </TooltipWrapper>
                </FlyoutLink>
              </span>
            </RoleRestricted>
          )
        }

        <RoleRestricted allowedRoles={[Roles.VOLUME_TARGET]}>
          {(showRemoveTargeted && !props.project.isEditRestricted) && (
            <span className={`bulk-action-group ms-3${hasSelectedRecords ? "" : " disabled"}`}>
              <FlyoutLink
                classNames="p-0"
                content={{
                  body: getDeleteNetNewProvider(),
                  title: `Remove Targeted Net New Servicing ${capitalizeFirstLetter(providerPlurality)}`,
                }}
                disabled={!hasSelectedRecords}
                testId="net-new-servicing-providers-panel__delete-flyout--target"
                size="20"
              >
                <TooltipWrapper text={`Stop targeting selected Net New Servicing ${providerPlurality}`}>
                  <QesIcon iconName={QesIcons.TrashV1} description="Remove" className="align-text-top me-1" /> Remove
                </TooltipWrapper>
              </FlyoutLink>
            </span>
          )}
        </RoleRestricted>
      </>
    );
  }, [selectedProviders, props.runConfiguration, props.project, displayedProviderType, projectionProvidersState]);

  function getNetNewProviderProjectionDetails() {

    const selectedNetNewProviderProjectIds: number[] = selectedProviders.map(nnp => +nnp);
    const selectedNetNewProviders: NetNewProvider[] = servicingProviders.filter(nnp => selectedNetNewProviderProjectIds.some(id => id === nnp.netNewProviderProjectId));

    return (
      <ProjectionProviderDetails
        {...props}
        accessPoints={convertServicingProvidersToAccessPoints(selectedNetNewProviders, props.providerType, props.specialtyGroupCode)}
        hasProvidersWithoutNPI={servicingProviders.some((provider) => provider.npi === undefined)}
        networkAssessmentAnalysisType={props.runConfiguration.networkAssessmentAnalysisType}
        priorProjectionProviderProjectIds={selectedProviders.map(nnp => +nnp)}
        priorProjectionProviderType={undefined} //This is for checking between exclude and targeted. We are only targeting for nnp
        projectId={props.project.projectId}
        projectionPanelMode={ProjectionPanelMode.Create}
        projectionProviderType={ProjectionProviderType.Target}
        providerNetworkType={props.providerType}
        formState={projectionProvidersState}
        formDispatch={projectionProvidersReducer}
        onProjectionProvidersCreated={handleNetNewProviderCreatedOrUpdated}
        specialtyGroupCode={props.specialtyGroupCode}
        isZipProject={false}
        showAffiliationsInsights={props.project.showAffiliationsInsights}
        isRunning={props.project?.isRunning}
      />
    );
  }

  function getDeleteNetNewProvider() {
    const selectedNetNewProviderProjectIds: number[] = selectedProviders.map(nnp => +nnp);

    return (
      <DeleteProjectionProvider
        netNewServicingProviderProjectIds={selectedNetNewProviderProjectIds}
        projectId={props.project.projectId}
        projectionProviderProjectIds={[]}
        projectionProviderType={ProjectionProviderType.Target}
        providerNetworkType={props.providerType}
        onProjectionProvidersDeleted={handleNetNewProvidersDeleted}
        wrapperComponentType="flyoutLink"
      />
    );
  }

  const providerSearchDescriptiveText = "This will search each provider's name, NPI, specialty, and address.";

  function renderSearchField() {
    if (props.project.showPciData) {
      return (
        <ProviderClaimsFilters
          clearFilters={displayedProviderTypeChanged}
          handleProviderSearch={handleProviderSearch}
          lastSearchValue={lastSearchValue}
          lastSelectedEffectivenessValues={selectedEffectivenessValues}
          lastSelectedEfficiencyValues={selectedEfficiencyValues}
          lastSelectedGainsPercent={gainsPercent}
          lastSelectedLineOfBusiness={selectedLineOfBusiness}
          lastSelectedVolumeLabels={selectedVolumeLabels}
          lastSelectedLocationConfidenceValues={selectedLocationConfidenceValues}
          lastSelectedMotiveAppropriatenessValues={selectedMotiveAppropriatenessValues}
          lastSelectedNewPatientClaimsValues={selectedNewPatientClaimsValues}
          lastSelectedCareJourneyCostValues={selectedCareJourneyCostValues}
          lastSelectedCareJourneyQualityValues={selectedCareJourneyQualityValues}
          lastSelectedProcedureValues={selectedProcedureValues}
          lastSelectedConditionValues={selectedConditionValues}
          topConditions={topConditions}
          topProcedures={topProcedures}
          showGainsPercent={true}
          tableType={TableType.NetNewProviders}
          validLoadStatus={!loading}
          showLocationConfidence={props.runConfiguration.showLocationConfidenceScores && props.project.showLocationConfidenceScores}
          updateChangeTracker={updateChangeTracker}
          showCareJourneyScores={props.runConfiguration.showCareJourneyScores}
          showSpecialtyPredictionModel={props.showSpecialtyPredictionModel}
          lastSelectedSpecialtyPredictionModelFilter={selectedSpecialtyPredictionModelFilter}
          lastSelectedConfidenceTierValues={selectedPgrConfidenceTiers}
          showConfidenceTier={props.showPgrConfidenceTier}
        />
      );
    } else if (props.showPgrConfidenceTier) {
      return (
        <ConfidenceTierSearchFilters
          clearSearch={displayedProviderTypeChanged}
          descriptiveText={providerSearchDescriptiveText}
          handleProviderSearch={handleConfidenceTierSearch}
          lastSearchValue={lastSearchValue}
          lastSelectedConfidenceTierValues={selectedPgrConfidenceTiers}
          tableType={TableType.NetNewProviders}
          updateChangeTracker={updateChangeTracker}
          validLoadStatus={!loading}
        />
      );
    } else {
      return (
        <SearchBox
          key={displayedProviderType}
          descriptiveText={providerSearchDescriptiveText}
          fieldsetClass="d-flex"
          inputLabel="Search Providers"
          testId={`net-new-provider-search`}
          onSubmit={(value: string) => { handleProviderSearch(value) }}
        />
      );
    }
  }

  function getPreviewTargetingContent() {
    const showAvgDist = props.runConfiguration.showDistance && props.project.showAverageDistance;
    const showAvgTime = props.runConfiguration.showTime && props.project.showAverageTime;
    const showWithTimeAndDistanceColumns = props.runConfiguration.showWithTimeAndDistanceColumns;

    const selectedNetNewProviderProjectIds: number[] = selectedProviders.map(nnp => +nnp);
    const selectedNetNewProviders: NetNewProvider[] = servicingProviders.filter(nnp => selectedNetNewProviderProjectIds.some(id => id === nnp.netNewProviderProjectId)).map(nnp => {
      nnp.providerId = nnp.netNewProviderProjectId
      return nnp;
    });

    const showNetNew = userContext.roles.includes(Roles.NET_NEW_PROVIDER_USER);

    return (
      <SimulateProviderTargeting
        processingContent={(() => getNetNewProviderProjectionDetails())()}
        projectionProviderType={ProjectionProviderType.Target}
        providerNetworkType={props.providerType}
        regionSetId={props.regionSetId}
        selectedProviders={selectedNetNewProviders}
        specialtyGroupCode={props.specialtyGroupCode}
        visibility={{ showAvgDist, showAvgTime, showNetNew, showWithTimeAndDistanceColumns }}
        user={userContext}
        formState={projectionProvidersState}
        formDispatch={projectionProvidersReducer}
        isZipProject={false}
      />
    );
  }

  async function handleNetNewProvidersDeleted(runProjection: boolean) {
    if (runProjection) {
      props.processProviderChanges(false);
    } else {
      props.setProjectHasUnprocessedProjectionProviders(true);
      setSelectedProviders([]);
      fetchProviders(displayedProviderType, 0, undefined);
    }     
  }

  function handleDistanceClick() {
    if (selectedProviders.length === 2) {
      const selectedProviderData = servicingProviders.filter(sp => selectedProviders.includes(sp.netNewProviderProjectId.toString()));
      const fromProvider = selectedProviderData[0];
      const toProvider = selectedProviderData[1];

      geoService.getDrivingDistance(fromProvider.longitude, fromProvider.latitude, toProvider.longitude, toProvider.latitude).then(result => {
        toast(result, toastId);
      });
    }
  }
  function setProviderType(providerData: NetNewProvider[]) {
    if (displayedProviderType === "targeted" && providerData.length === 0) {
      setDisplayedProviderType("nonTargeted");
      setDisplayedProviderTypeChanged(true);
    } else if (displayedProviderType === "nonTargeted" && providerData.length === 0) {
      setDisplayedProviderType("targeted");
      setDisplayedProviderTypeChanged(true);
    }
  }
  function setProvidersData(providerData: NetNewProvider[]) {
    setDisplayedProviderData(convertProviderToTableData(providerData as NetNewProvider[]));
    setTotalRecords(providerData.length);
  }
  function handleRunConfigurationChange() {
    if (projectContext.runConfiguration.isProjected || projectContext.runConfiguration.isProjectedTermed) {
      setDisabledGainsFilter(true);
    }
    else {
      setDisabledGainsFilter(false);
    }
  }

  const handleProviderSearch = (searchValue: string,
    selectedEffectivenessValues?: EffectivenessEfficiencyValueEnum[],
    selectedEfficiencyValues?: EffectivenessEfficiencyValueEnum[],
    selectedVolumeLabels?: ClaimsActivityVolumeLabelEnum[],
    selectedLineOfBusiness?: ClaimsActivityLineOfBusinessTypeEnum,
    selectedLocationConfidenceValues?: LocationConfidenceScoresLabelEnum[],
    selectedMotiveAppropriatenessValues?: MotiveScoreValueEnum[],
    selectedCareJourneyCostValues?: CareJourneyScoresValueEnum[],
    selectedCareJourneyQualityValues?: CareJourneyScoresValueEnum[],
    gainsPercent?: GainsPercentEnum,
    selectedNewPatientClaimsValues?: NewPatientClaimsEnum[],
    selectedProcedureValues?: string[],
    selectedConditionValues?: string[],
    selectedSpecialtyPredictionModelFilter?: SpecialtyPredictionModelEnum,
    selectedPgrConfidenceTiers?: number[]
  ) => {
    fetchProviders(
      displayedProviderType,
      0, // a new filter starts from the first page
      undefined,
      undefined,
      searchValue,
      gainsPercent,
      selectedEffectivenessValues,
      selectedEfficiencyValues,
      selectedVolumeLabels,
      selectedLineOfBusiness,
      selectedLocationConfidenceValues,
      selectedMotiveAppropriatenessValues,
      selectedNewPatientClaimsValues,
      selectedProcedureValues,
      selectedCareJourneyCostValues,
      selectedCareJourneyQualityValues,
      selectedConditionValues,
      selectedSpecialtyPredictionModelFilter,
      selectedPgrConfidenceTiers
    );
  }

  const handleConfidenceTierSearch = (searchValue: string, selectedPgrConfidenceTiers: number[]) => {
    handleProviderSearch(
      searchValue,
      undefined, undefined, undefined, undefined, undefined, undefined, undefined,
      undefined, undefined, undefined, undefined, undefined, undefined,
      selectedPgrConfidenceTiers
    );
  }

  const accordionTestId = useMemo(() => {
    return "net-new-servicing-providers-panel";
  }, [props.providerType]);

  const targetedToggle = useMemo(() => {
    fetchProviders(displayedProviderType);
    return (
      <div className="clearfix">
        <div className="pull-right">
          <Toggle
            label="Targeted"
            active={displayedProviderType === "targeted"}
            onChange={() => {
              setDisplayedProviderType(displayedProviderType === "targeted" ? "nonTargeted" : "targeted");
              setSelectedProviders([]);
              setLastSearchValue("");
              setSelectedEffectivenessValues(undefined);
              setSelectedEfficiencyValues(undefined);
              setSelectedVolumeLabels(undefined);
              setSelectedLineOfBusiness(undefined);
              setSelectedLocationConfidenceValues(undefined);
              setSelectedMotiveAppropriatenessValues(undefined);
              setSelectedNewPatientClaimsValues(undefined);
              setSelectedCareJourneyCostValues(undefined);
              setSelectedCareJourneyQualityValues(undefined);
              setDisplayedProviderTypeChanged(true);
              setSelectedProcedureValues([]);
              setSelectedConditionValues([]);
            }}
            containerClassName="m-3"
            testId="net-new-targeted-toggle"
          />
        </div>
      </div>
    );
  }, [displayedProviderType]);

  const renderDropdownAndToggles = () => {
    const networkPrefix = "net new servicing";
    const leadText = (props.accessSummary)
      ? (props.accessSummary.memberAccessPercentage < 100)
        ? (<>The following {networkPrefix} providers are capable of servicing beneficiaries.  <strong className="text-success">Green </strong>impacts access to at least {props.accessSummary.passPercentage}%, <strong className="text-warning">yellow </strong>impacts access to at least 25%, otherwise <strong className="text-danger">red</strong>.</>)
        : (<>The following {networkPrefix} providers are potential alternatives to the current servicing providers.  <strong className="text-success">Green </strong>serves at least {props.accessSummary.passPercentage}%, <strong className="text-warning">yellow </strong>serves at least 25%, otherwise <strong className="text-danger">red</strong>.</>)
      : null;

    const hasNetNewServicingProviders = servicingProviders.length > 0;
    return (
      <>
        <p className={hasNetNewServicingProviders ? "mb0" : ""}>{leadText}</p>
        {(!props.project.showPciData) && (
          <div className="d-flex justify-content-end">
            <div>
              <label className="label-s me-3 d-inline-block" htmlFor={`claims-activity__filters--gains-percent-${ProviderNetworkTypeEnum.NetNew}`}>Display</label>
              <TooltipWrapper text={disabledGainsFilter ? "The Gains Percent filter is unavailable for projected and projected-termed timeslices." : ""}>
                <GainsPercentFilter
                  containerClassName="d-inline-block"
                  gainsPercent={gainsPercent}
                  hideLabel={true}
                  setGainsPercent={setPercentGains}
                  testId={`claims-activity__filters--gains-percent-${ProviderNetworkTypeEnum.NetNew}`}
                  disabledGainsFilter={disabledGainsFilter}
                >
                </GainsPercentFilter>
              </TooltipWrapper>
            </div>

          </div>
        )}
        {targetedToggle}
      </>
    );
  }

  const setPercentGains = (gainsPercent: GainsPercentEnum) => {
    setGainsPercent(gainsPercent);
    fetchProviders(displayedProviderType, 0, undefined, undefined, undefined, gainsPercent)
  }

  return userContext.roles.includes(Roles.VOLUME_TARGET) && userContext.roles.includes(Roles.NET_NEW_PROVIDER_USER) ? (
    <>
      <AccordionContainer>
        <Accordion
          defaultOpen={false}
          title={accordionTitle}
          onPanelWillOpen={!displayedProviderData && fetchProviders}
          onPanelDidOpen={() => setIsOpen(true)}
          onPanelDidClose={() => setIsOpen(false)}
          testId={accordionTestId}
        >
          {
            displayedProviderData && props.viewableFields ?
              props.editRestricted || props.readOnly ?
                <p className="copy-text-default mb-0">Net New Servicing Provider Results are currently being
                  calculated.</p> :
                <>
                  {renderSearchField()}
                  {renderDropdownAndToggles()}
                  {
                    (
                      <>
                        <NetNewServicingProviderTable
                          accessSummary={props.accessSummary}
                          bulkActionBar={getBulkActionHeader}
                          data={displayedProviderData}
                          displayedProviderType={displayedProviderType}
                          providerType={props.providerType}
                          viewableFields={props.viewableFields}
                          handleSort={(col, direction) => {
                            fetchProviders(displayedProviderType, 0, col, direction)
                          }}
                          loading={loading}
                          onSelectedProvidersChanged={setSelectedProviders}
                          project={props.project}
                          runConfiguration={props.runConfiguration}
                          selectedProviders={selectedProviders}
                          setSkip={(newSkip: number) => fetchProviders(displayedProviderType, newSkip)}
                          skip={skip}
                          tableType={TableType.NetNewProviders}
                          take={take}
                          totalRecords={totalRecords}
                          showProviderValidations={props.showProviderValidations}
                          showSpecialtyPredictionModel={props.showSpecialtyPredictionModel}
                          showPgrConfidenceTier={props.showPgrConfidenceTier}
                        />
                      </>
                    )}
                </> :
              <LoadingSpinner />
          }
        </Accordion>
      </AccordionContainer>
      <Flyout
        triggerElement={flyoutButtonRefs.current[selectedFlyoutButtonRef]}
        content={{
          body: <ProjectionProviderDetails
            {...props}
            accessPoints={convertServicingProvidersToAccessPoints(selectedServicingProviders, props.providerType, props.specialtyGroupCode)}
            hasProvidersWithoutNPI={selectedNetNewProvider?.npi != undefined}
            networkAssessmentAnalysisType={props.runConfiguration.networkAssessmentAnalysisType}
            projectId={props.project.projectId}
            projectionPanelMode={ProjectionPanelMode.Edit}
            projectionProvider={selectedNetNewProvider}
            projectionProviderType={ProjectionProviderType.Target}
            providerNetworkType={props.providerType}
            formState={projectionProvidersState}
            formDispatch={projectionProvidersReducer}
            onProjectionProviderUpdated={handleNetNewProviderCreatedOrUpdated}
            specialtyGroupCode={props.specialtyGroupCode}
            isZipProject={false}
            showAffiliationsInsights={props.project.showAffiliationsInsights}
            isRunning={props.project?.isRunning}
          />,
          title: "Target Details",
        }}
      />
    </>
  ) : <></>;
}

const WrappedNetNewServicingProvidersPanel: React.FC<NetNewServicingProvidersPanelProps> = (props: NetNewServicingProvidersPanelProps) => {
  return (
    <FlyoutContextProvider>
      <NetNewServicingProvidersPanel {...props} />
    </FlyoutContextProvider>
  )
};

export { WrappedNetNewServicingProvidersPanel as NetNewServicingProvidersPanel };
